import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, concatLatestFrom } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { TicketService } from 'src/app/travelx/tickets/ticket.service';
import * as TicketSettingsActions from 'src/app/root-store/ticket-store/actions/tickets.actions';
import { TicketCategory } from 'src/app/travelx/models/ticket-category.model';
import { TicketSettingsCategoryType } from 'src/app/travelx/models/ticket-settings-category-type.model';
import { selectTagTypes } from 'src/app/root-store/ticket-store/tickets.selectors';
import { HttpResponseHelpers } from 'src/app/shared/helpers';
import { Store } from '@ngrx/store';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';

@Injectable()
export class TicketSettingsEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _ticketService: TicketService,
        private readonly _store: Store
    ) {}

    getTicketSettingsCategories$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                TicketSettingsActions.loadTicketSettingsCategories,
                TicketSettingsActions.createTicketCategorySuccess,
                TicketSettingsActions.editTicketCategorySuccess,
                TicketSettingsActions.createTicketSubCategorySuccess,
                TicketSettingsActions.editTicketSubCategorySuccess,
                TicketSettingsActions.deleteTicketCategorySuccess
            ),
            switchMap(() => {
                return this._ticketService.getTicketCategories().pipe(
                    map((result: TicketCategory[]) =>
                        TicketSettingsActions.loadTicketSettingsCategoriesSuccess({ settingsCategories: result })
                    ),
                    catchError((error) =>
                        of(
                            TicketSettingsActions.loadTicketSettingsCategoriesFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    getTicketSettingsCategoryTypes$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(TicketSettingsActions.loadTicketCategoryTypes),
            switchMap(() => {
                return this._ticketService.getCategoryTypes().pipe(
                    map((result: TicketSettingsCategoryType[]) =>
                        TicketSettingsActions.loadTicketCategoryTypesSuccess({ categoryTypes: result })
                    ),
                    catchError((error) =>
                        of(
                            TicketSettingsActions.loadTicketCategoryTypesFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    createTicketCategory$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(TicketSettingsActions.createTicketCategory),
            switchMap((action) =>
                this._ticketService
                    .createTicketCategory(
                        action.categoryTypeId,
                        action.defaultAssigneeId,
                        action.name,
                        action.parentCategoryId,
                        action.isAutoLock
                    )
                    .pipe(
                        switchMap((id) => [
                            TicketSettingsActions.createTicketCategorySuccess({
                                id,
                                message: 'Successfully created Ticket Category'
                            })
                        ]),
                        catchError((error) =>
                            of(
                                TicketSettingsActions.createTicketCategoryFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    )
            )
        );
    });

    editTicketCategory$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(TicketSettingsActions.editTicketCategory),
            switchMap((action) =>
                this._ticketService
                    .editTicketCategory(
                        action.id,
                        action.categoryTypeId,
                        action.defaultAssigneeId,
                        action.name,
                        action.parentCategoryId,
                        action.isAutoLock
                    )
                    .pipe(
                        switchMap((id) => [
                            TicketSettingsActions.editTicketCategorySuccess({
                                id,
                                message: 'Successfully updated Ticket Category'
                            })
                        ]),
                        catchError((error) =>
                            of(
                                TicketSettingsActions.editTicketCategoryFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    )
            )
        );
    });

    deleteTicketCategory$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(TicketSettingsActions.deleteTicketCategory),
            switchMap((action) =>
                this._ticketService.deleteTicketCategory(action.id).pipe(
                    switchMap(() => [
                        TicketSettingsActions.deleteTicketCategorySuccess({
                            message: 'Successfully deleted Ticket Category'
                        })
                    ]),
                    catchError((error) =>
                        of(
                            TicketSettingsActions.deleteTicketCategoryFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                )
            )
        );
    });

    createTicketSubCategory$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(TicketSettingsActions.createTicketSubCategory),
            switchMap((action) =>
                this._ticketService
                    .createTicketCategory(
                        action.categoryTypeId,
                        action.defaultAssigneeId,
                        action.name,
                        action.parentCategoryId,
                        action.isAutoLock
                    )
                    .pipe(
                        switchMap((id) => [
                            TicketSettingsActions.createTicketSubCategorySuccess({
                                id,
                                message: 'Successfully created Ticket Subcategory'
                            })
                        ]),
                        catchError((error) =>
                            of(
                                TicketSettingsActions.createTicketSubCategoryFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    )
            )
        );
    });

    editTicketSubCategory$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(TicketSettingsActions.editTicketSubCategory),
            switchMap((action) =>
                this._ticketService
                    .editTicketCategory(
                        action.id,
                        action.categoryTypeId,
                        action.defaultAssigneeId,
                        action.name,
                        action.parentCategoryId,
                        action.isAutoLock
                    )
                    .pipe(
                        switchMap((id) => [
                            TicketSettingsActions.editTicketSubCategorySuccess({
                                id,
                                message: 'Successfully updated Ticket Subcategory'
                            })
                        ]),
                        catchError((error) =>
                            of(
                                TicketSettingsActions.editTicketSubCategoryFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    )
            )
        );
    });

    getTicketTagTypes$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                TicketSettingsActions.loadTagTypes,
                TicketSettingsActions.addTagTypeSuccess,
                TicketSettingsActions.updateTagTypeSuccess,
                TicketSettingsActions.deleteTagTypeSuccess
            ),
            concatLatestFrom(() => this._store.select(selectTagTypes)),
            concatMap(([action, tagTypes]) => {
                if (!tagTypes.length || action.type !== TicketSettingsActions.loadTagTypes.type) {
                    return this._ticketService.getTicketTagTypes().pipe(
                        map((tagTypes) => TicketSettingsActions.loadTagTypesSuccess({ tagTypes })),
                        catchError((error) =>
                            of(
                                TicketSettingsActions.loadTagTypesFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    );
                } else {
                    return of(TicketSettingsActions.loadTagTypesSuccess({ tagTypes }));
                }
            })
        );
    });

    getTicketTagTypesForCategory$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(TicketSettingsActions.loadTagTypesForCategory),
            switchMap((action) => {
                return this._ticketService.getTicketTagTypesForCategory(action.categoryId).pipe(
                    map((tagTypesForCategory) =>
                        TicketSettingsActions.loadTagTypesForCategorySuccess({ tagTypesForCategory })
                    ),
                    catchError((error) =>
                        of(
                            TicketSettingsActions.loadTagTypesForCategoryFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    updateTicketTagType$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(TicketSettingsActions.updateTagType),
            concatMap((action) => {
                return this._ticketService.updateTicketTagType(action.tagType).pipe(
                    map((_) => {
                        return TicketSettingsActions.updateTagTypeSuccess({
                            message: 'Successfully updated Tag'
                        });
                    }),
                    catchError((error) =>
                        of(
                            TicketSettingsActions.updateTagTypeFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    addTicketTagType$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(TicketSettingsActions.addTagType),
            concatMap((action) => {
                return this._ticketService.addTicketTagType(action.tagType).pipe(
                    map((_) =>
                        TicketSettingsActions.addTagTypeSuccess({
                            message: 'Successfully created Tag'
                        })
                    ),
                    catchError((error) =>
                        of(
                            TicketSettingsActions.addTagTypeFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    deleteTicketTagType$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(TicketSettingsActions.deleteTagType),
            concatMap((action) => {
                return this._ticketService.deleteTicketTagType(action.id).pipe(
                    map((_) =>
                        TicketSettingsActions.deleteTagTypeSuccess({
                            message: 'Successfully deleted Tag'
                        })
                    ),
                    catchError((error) =>
                        of(
                            TicketSettingsActions.deleteTagTypeFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    showMessage$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                TicketSettingsActions.createTicketCategorySuccess,
                TicketSettingsActions.createTicketSubCategorySuccess,
                TicketSettingsActions.editTicketCategorySuccess,
                TicketSettingsActions.editTicketSubCategorySuccess,
                TicketSettingsActions.deleteTicketCategorySuccess,
                TicketSettingsActions.addTagTypeSuccess,
                TicketSettingsActions.updateTagTypeSuccess,
                TicketSettingsActions.deleteTagTypeSuccess
            ),
            map((action) => showToasterMessage({ message: action.message }))
        );
    });

    showMessageFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                TicketSettingsActions.createTicketCategoryFailure,
                TicketSettingsActions.createTicketSubCategoryFailure,
                TicketSettingsActions.editTicketCategoryFailure,
                TicketSettingsActions.editTicketSubCategoryFailure,
                TicketSettingsActions.deleteTicketCategoryFailure,
                TicketSettingsActions.addTagTypeFailure,
                TicketSettingsActions.updateTagTypeFailure,
                TicketSettingsActions.deleteTagTypeFailure
            ),
            map((action) => showToasterMessage({ message: action.error as string }))
        );
    });
}
